import { type ReactNode } from 'react';
import {
  AUDIT_TRAIL_DOCUMENT_DOWNLOADED,
  AUDIT_TRAIL_DOCUMENT_VIEWED,
  AUDIT_TRAIL_REVIEW_APPROVED,
  AUDIT_TRAIL_REVIEW_REJECTED,
  AUDIT_TRAIL_SIGN_STARTED,
  AUDIT_TRAIL_SIGNING_CANCELLED,
  AUDIT_TRAIL_SIGNING_COMPLETED,
  AUDIT_TRAIL_SIGNING_EXPIRED,
  type RecentActivityItem,
} from 'xosign-types';
import IcCheck from '@/assets/icons/Check.svg?react';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcEyeOpened from '@/assets/icons/EyeOpened.svg?react';
import IcPaperPlane from '@/assets/icons/PaperPlane.svg?react';

export type Tone = 'success' | 'error' | 'neutral' | 'brand';

export interface RowCopy {
  tone: Tone;
  icon: ReactNode;
  text: string;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const buildCopy = (item: RecentActivityItem, t: Translate): RowCopy | null => {
  const actor = item.actorName?.trim() ?? '';
  const signing = item.signing.name;
  const tArgs = actor ? { actor, signing } : { signing };
  const key = (slug: string) =>
    `common:dashboard.recentActivity.${slug}.${actor ? 'withActor' : 'withoutActor'}`;

  switch (item.action) {
    case AUDIT_TRAIL_SIGNING_COMPLETED:
      return {
        tone: 'success',
        icon: <IcCheck />,
        text: t(key('signed'), tArgs),
      };
    case AUDIT_TRAIL_REVIEW_APPROVED:
      return {
        tone: 'success',
        icon: <IcCheck />,
        text: t(key('approved'), tArgs),
      };
    case AUDIT_TRAIL_REVIEW_REJECTED:
      return {
        tone: 'error',
        icon: <IcCross />,
        text: t(key('declined'), tArgs),
      };
    case AUDIT_TRAIL_SIGNING_CANCELLED:
      return {
        tone: 'error',
        icon: <IcCross />,
        text: t(key('cancelled'), tArgs),
      };
    case AUDIT_TRAIL_SIGNING_EXPIRED:
      return {
        tone: 'error',
        icon: <IcCross />,
        text: t(key('expired'), tArgs),
      };
    case AUDIT_TRAIL_SIGN_STARTED:
      return {
        tone: 'brand',
        icon: <IcPaperPlane />,
        text: t(key('signStarted'), tArgs),
      };
    case AUDIT_TRAIL_DOCUMENT_VIEWED:
      return {
        tone: 'neutral',
        icon: <IcEyeOpened />,
        text: t(key('viewed'), tArgs),
      };
    case AUDIT_TRAIL_DOCUMENT_DOWNLOADED:
      return {
        tone: 'neutral',
        icon: <IcEyeOpened />,
        text: t(key('downloaded'), tArgs),
      };
    default:
      return null;
  }
};

export default buildCopy;
